import { Injectable } from '@angular/core';

import { filter, interval, Subscription, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FocusService {
  IONIC_FOCUS_RETRY_EVERY_MS = 100;
  IONIC_FOCUS_ATTEMPT_MAX = 50;

  private focusSubscription?: Subscription;

  private getActiveElementDeep(): Element | null {
    let activeElement = document.activeElement;
    while (
      activeElement &&
      activeElement.shadowRoot &&
      activeElement.shadowRoot.activeElement
    ) {
      activeElement = activeElement.shadowRoot.activeElement;
    }
    return activeElement;
  }

  // Happens asynchronously.
  // If you want to set AUTOFOCUS, do not use this. This is for a one-time focus only.
  focus({
    element,
    shadowRootSelector,
    childSelector,
  }: {
    element: HTMLElement;
    shadowRootSelector?: string;
    childSelector?: string;
  }) {
    // Logic:
    // When an element is queued, try every 100ms, max 50x (5s total) to set the focus() to the element.
    // When another element is queued, this timer should reset, the previous element should no longer be tried.

    // First we close existing attempts, if any
    this.focusSubscription?.unsubscribe();

    // Then we replace it with a new subscription that attempts for every 100ms.
    this.focusSubscription = interval(this.IONIC_FOCUS_RETRY_EVERY_MS)
      .pipe(
        take(this.IONIC_FOCUS_ATTEMPT_MAX),
        filter(() => {
          // Query the element here because the element might not be ready yet when this method is called.
          const focusElement = shadowRootSelector
            ? element.shadowRoot?.querySelector(shadowRootSelector)
            : childSelector
              ? element.querySelector(childSelector)
              : element;

          if (!focusElement) {
            return false;
          }

          (focusElement as HTMLElement).focus();
          const activeElement = this.getActiveElementDeep();
          return focusElement === activeElement;
        }),
        take(1),
      )
      .subscribe();
  }
}
